/**
 * Phone number normalization + SHA-256 hashing for contact matching.
 *
 * Uses libphonenumber-js (same library as WhatsApp/Telegram) to normalize
 * all phone numbers to E.164 format before hashing. This handles:
 * - Local numbers without country code ("050 123 4567" → "+971501234567")
 * - Trunk zero after country code ("+971 050 123 4567" → "+971501234567")
 * - International prefix "00" ("00971501234567" → "+971501234567")
 * - Various formatting (spaces, dashes, parens)
 */

import { parsePhoneNumber } from 'libphonenumber-js/min';
import type { CountryCode } from 'libphonenumber-js/min';

export type { CountryCode };

/**
 * Normalize a phone number to E.164 format.
 *
 * Uses the importing user's country as default when the number has no
 * international prefix — same approach as WhatsApp (which uses SIM country).
 *
 * Returns null if the number cannot be parsed or validated.
 */
export function normalizeToE164(
  raw: string,
  defaultCountry: CountryCode = 'AE'
): string | null {
  let cleaned = raw.trim();
  if (!cleaned) return null;

  // Convert "00" international dialing prefix to "+"
  if (cleaned.startsWith('00') && !cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(2);
  }

  // Try parsing with default country
  try {
    let parsed = parsePhoneNumber(cleaned, defaultCountry);

    // If parsing succeeded but number has "+" with embedded trunk zero
    // (e.g., "+971 050 123 4567"), libphonenumber usually handles it.
    // As a fallback, strip the trunk zero and retry.
    if (!parsed?.isValid() && cleaned.startsWith('+')) {
      const withoutTrunkZero = cleaned.replace(/^(\+\d{1,3})\s*0/, '$1');
      if (withoutTrunkZero !== cleaned) {
        try {
          parsed = parsePhoneNumber(withoutTrunkZero, defaultCountry);
        } catch {
          // ignore
        }
      }
    }

    if (parsed?.isValid()) {
      return parsed.number; // E.164: "+971501234567"
    }
  } catch {
    // parsePhoneNumber throws on completely unparseable input
  }

  return null;
}

/**
 * Derive the ISO country code from a stored E.164 phone number.
 * Returns 'AE' as fallback if parsing fails.
 */
export function getCountryFromPhone(phone: string): CountryCode {
  try {
    const parsed = parsePhoneNumber(phone);
    return parsed?.country || 'AE';
  } catch {
    return 'AE';
  }
}

/**
 * Normalize a phone number to E.164 and return its SHA-256 hex digest.
 *
 * If E.164 normalization succeeds, hashes the E.164 string (e.g., "+971501234567").
 * If normalization fails, falls back to hashing digits-only for backwards compat.
 */
export async function hashPhone(
  phone: string,
  defaultCountry: CountryCode = 'AE'
): Promise<string> {
  const e164 = normalizeToE164(phone, defaultCountry);
  const toHash = e164 ?? phone.replace(/\D/g, '');
  return sha256Hex(toHash);
}

async function sha256Hex(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const buffer = await crypto.subtle.digest('SHA-256', encoded);
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
